import React, { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ListChecks, Plus, Trash2, Bell, Download, BarChart3, ShieldCheck,
  User, X, Loader2, PlusCircle,
} from "lucide-react";
import { toast } from "sonner";
import { ZuordnungPicker, ZuordnungLabel } from "@/components/portal/ZuordnungPicker";

const FRAGE_TYPEN = [
  { value: "freitext", label: "Freitext" },
  { value: "single", label: "Single Choice" },
  { value: "multi", label: "Multi Choice" },
  { value: "skala5", label: "Skala 1–5" },
  { value: "jaNein", label: "Ja / Nein" },
  { value: "zahl", label: "Zahl" },
  { value: "datum", label: "Datum" },
];

export const AdminUmfragenTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/surveys?include_inaktiv=true");
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setNewOpen(true)} data-testid="umfrage-neu">
          <Plus className="h-4 w-4 mr-2" /> Neue Umfrage
        </Button>
      </div>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-5 w-5 text-primary" /> Umfragen ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Noch keine Umfragen angelegt.
            </p>
          ) : (
            <div className="space-y-2">
              {items.map((s) => (
                <div key={s.id} data-testid={`umfrage-row-${s.id}`}
                  className="rounded-lg border bg-card p-3 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{s.titel}</span>
                      {s.modus === "anonym"
                        ? <Badge variant="outline" className="text-[10px]"><ShieldCheck className="h-3 w-3 mr-1" /> anonym</Badge>
                        : <Badge variant="outline" className="text-[10px]"><User className="h-3 w-3 mr-1" /> namentlich</Badge>
                      }
                      {!s.aktiv && <Badge variant="outline">inaktiv</Badge>}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <ZuordnungLabel typ={s.zuordnung_typ} ids={s.zuordnung_ids} />
                      <span>· {s.fragen?.length || 0} Fragen</span>
                      <span>· {s.antworten_count || 0} Antworten</span>
                      {s.deadline && <span>· Deadline: {fmtDate(s.deadline)}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => setDetail(s)}
                      data-testid={`umfrage-detail-${s.id}`}>
                      <BarChart3 className="h-4 w-4 mr-1" /> Auswerten
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {newOpen && (
        <NeueUmfrageDialog
          onClose={() => setNewOpen(false)}
          onDone={() => { setNewOpen(false); laden(); }} />
      )}
      {detail && (
        <UmfrageDetailDialog survey={detail} onClose={() => setDetail(null)}
          onChanged={() => { setDetail(null); laden(); }} />
      )}
    </div>
  );
};

const FrageEditor = ({ frage, onChange, onRemove }) => {
  const setOpt = (idx, val) => {
    const next = [...(frage.optionen || [])];
    next[idx] = val;
    onChange({ ...frage, optionen: next });
  };
  const addOpt = () => onChange({ ...frage, optionen: [...(frage.optionen || []), ""] });
  const rmOpt = (idx) => onChange({
    ...frage, optionen: (frage.optionen || []).filter((_, i) => i !== idx),
  });
  const braucht = frage.typ === "single" || frage.typ === "multi";
  const zahl = frage.typ === "zahl";
  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Input value={frage.frage || ""} placeholder="Frage eingeben …"
            onChange={(e) => onChange({ ...frage, frage: e.target.value })} />
        </div>
        <Select value={frage.typ} onValueChange={(v) => onChange({ ...frage, typ: v, optionen: [] })}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FRAGE_TYPEN.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="icon" variant="ghost" onClick={onRemove}
          data-testid="frage-remove">
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
      {braucht && (
        <div className="pl-3 space-y-1">
          {(frage.optionen || []).map((o, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input value={o} onChange={(e) => setOpt(idx, e.target.value)}
                placeholder={`Option ${idx + 1}`} />
              <Button size="icon" variant="ghost" onClick={() => rmOpt(idx)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addOpt}>
            <PlusCircle className="h-3.5 w-3.5 mr-1" /> Option
          </Button>
        </div>
      )}
      {zahl && (
        <div className="flex gap-2">
          <Input placeholder="min" type="number" value={frage.min ?? ""}
            onChange={(e) => onChange({ ...frage, min: e.target.value === "" ? null : Number(e.target.value) })} />
          <Input placeholder="max" type="number" value={frage.max ?? ""}
            onChange={(e) => onChange({ ...frage, max: e.target.value === "" ? null : Number(e.target.value) })} />
        </div>
      )}
      <label className="flex items-center gap-2 text-xs">
        <Switch checked={frage.pflicht !== false}
          onCheckedChange={(v) => onChange({ ...frage, pflicht: v })} />
        Pflichtfrage
      </label>
    </div>
  );
};

const NeueUmfrageDialog = ({ onClose, onDone }) => {
  const [titel, setTitel] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [modus, setModus] = useState("namentlich");
  const [zuord, setZuord] = useState({ zuordnung_typ: "alle", zuordnung_ids: [] });
  const [gueltigAb, setGueltigAb] = useState("");
  const [gueltigBis, setGueltigBis] = useState("");
  const [deadline, setDeadline] = useState("");
  const [reminderTage, setReminderTage] = useState("7,3,1");
  const [benachrichtigen, setBenachrichtigen] = useState(false);
  const [fragen, setFragen] = useState([
    { typ: "freitext", frage: "", pflicht: true, optionen: [] },
  ]);
  const [running, setRunning] = useState(false);

  const setFrage = (idx, f) => setFragen((fs) => fs.map((x, i) => i === idx ? f : x));
  const addFrage = () => setFragen((fs) => [...fs, { typ: "freitext", frage: "", pflicht: true, optionen: [] }]);
  const rmFrage = (idx) => setFragen((fs) => fs.filter((_, i) => i !== idx));

  const speichern = async () => {
    if (!titel.trim()) { toast.error("Bitte Titel eingeben."); return; }
    if (fragen.length === 0) { toast.error("Mindestens eine Frage."); return; }
    if (zuord.zuordnung_typ !== "alle" && zuord.zuordnung_ids.length === 0) {
      toast.error("Zielgruppe konkretisieren."); return;
    }
    const reminder = (reminderTage || "").split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => Number.isFinite(n) && n > 0);
    setRunning(true);
    try {
      await api.post("/surveys", {
        titel, beschreibung, modus,
        zuordnung_typ: zuord.zuordnung_typ,
        zuordnung_ids: zuord.zuordnung_ids,
        gueltig_ab: gueltigAb || null,
        gueltig_bis: gueltigBis || null,
        deadline: deadline || null,
        reminder_tage_vorher: reminder,
        fragen,
        benachrichtigen,
      });
      toast.success("Umfrage angelegt.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Neue Umfrage</DialogTitle>
          <DialogDescription>
            Zwei Modi: <strong>namentlich</strong> (mit Liefernummer) oder
            <strong> anonym</strong> (keine Identität gespeichert).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Titel</Label>
              <Input value={titel} onChange={(e) => setTitel(e.target.value)}
                placeholder="z. B. Zufriedenheit Milchtankleerung" data-testid="umfrage-titel" />
            </div>
            <div>
              <Label className="text-sm">Modus</Label>
              <Select value={modus} onValueChange={setModus}>
                <SelectTrigger data-testid="umfrage-modus"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="namentlich">Namentlich</SelectItem>
                  <SelectItem value="anonym">Anonym</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm">Beschreibung</Label>
            <Textarea rows={2} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} />
          </div>
          <ZuordnungPicker value={zuord} onChange={setZuord} testIdPrefix="umfrage-zuord" />
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-sm">Gültig ab</Label>
              <Input type="date" value={gueltigAb} onChange={(e) => setGueltigAb(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Gültig bis</Label>
              <Input type="date" value={gueltigBis} onChange={(e) => setGueltigBis(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Deadline</Label>
              <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Erinnerung (Tage vorher)</Label>
              <Input value={reminderTage} onChange={(e) => setReminderTage(e.target.value)}
                placeholder="7,3,1" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm">Fragen ({fragen.length})</Label>
              <Button size="sm" variant="outline" onClick={addFrage} data-testid="frage-add">
                <PlusCircle className="h-3.5 w-3.5 mr-1" /> Frage
              </Button>
            </div>
            <div className="space-y-2">
              {fragen.map((f, idx) => (
                <FrageEditor key={idx} frage={f}
                  onChange={(nf) => setFrage(idx, nf)}
                  onRemove={() => rmFrage(idx)} />
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 rounded-md border p-2 bg-secondary/40 cursor-pointer">
            <Switch checked={benachrichtigen} onCheckedChange={setBenachrichtigen} />
            <div className="flex-1">
              <p className="text-sm flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> Lieferanten benachrichtigen</p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={speichern} disabled={running} data-testid="umfrage-speichern">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Veröffentlichen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UmfrageDetailDialog = ({ survey, onClose, onChanged }) => {
  const [erg, setErg] = useState(null);
  const [loading, setLoading] = useState(true);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/surveys/${survey.id}/ergebnisse`);
      setErg(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [survey.id]);
  useEffect(() => { laden(); }, [laden]);

  const download = async () => {
    try {
      const res = await api.get(`/surveys/${survey.id}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `umfrage_${survey.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loeschen = async () => {
    if (!window.confirm("Umfrage inkl. aller Antworten löschen?")) return;
    try {
      await api.delete(`/surveys/${survey.id}`);
      toast.success("Umfrage gelöscht.");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" /> {survey.titel}
          </DialogTitle>
          <DialogDescription>
            {survey.modus === "anonym"
              ? "Anonyme Umfrage – keine Identitätsdaten gespeichert."
              : "Namentliche Umfrage – Antworten pro Liefernummer."}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : erg ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatCard label="Antworten" value={erg.antworten_gesamt} />
              <StatCard label="Fragen" value={erg.survey.fragen?.length || 0} />
              <StatCard label="Modus" value={survey.modus === "anonym" ? "anonym" : "namentlich"} />
            </div>
            {erg.aggregat?.length > 0 && (
              <div className="space-y-3">
                {erg.aggregat.map((a, i) => (
                  <div key={i} className="rounded-md border p-3">
                    <p className="font-medium text-sm">{a.frage}
                      <span className="text-xs text-muted-foreground ml-2">({a.typ})</span>
                    </p>
                    <VerteilungAnzeige stat={a} />
                  </div>
                ))}
              </div>
            )}
            {survey.modus === "namentlich" && erg.antworten?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1">Einzelantworten</p>
                <div className="rounded-md border max-h-60 overflow-y-auto text-xs">
                  {erg.antworten.map((r) => (
                    <div key={r.id} className="border-t first:border-0 p-2">
                      <div className="font-medium">{r.lieferant_name} · Nr. {r.liefernummer}
                        <span className="ml-2 text-muted-foreground font-normal">{new Date(r.submitted_am).toLocaleString("de-DE")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={loeschen} data-testid="umfrage-loeschen">
            <Trash2 className="h-4 w-4 mr-2" /> Löschen
          </Button>
          <Button variant="secondary" onClick={download} data-testid="umfrage-csv">
            <Download className="h-4 w-4 mr-2" /> CSV Export
          </Button>
          <Button onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ label, value }) => (
  <div className="rounded-md border p-2">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold">{value}</p>
  </div>
);

const VerteilungAnzeige = ({ stat }) => {
  if (stat.verteilung) {
    const total = Object.values(stat.verteilung).reduce((a, b) => a + b, 0) || 1;
    return (
      <div className="mt-1 space-y-1">
        {Object.entries(stat.verteilung).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="text-xs w-24 truncate">{k}</span>
            <div className="flex-1 h-2 bg-secondary rounded overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${(v / total) * 100}%` }} />
            </div>
            <span className="text-xs w-8 text-right font-mono">{v}</span>
          </div>
        ))}
        {typeof stat.durchschnitt === "number" && (
          <p className="text-xs text-muted-foreground">Ø {stat.durchschnitt}</p>
        )}
      </div>
    );
  }
  if (stat.beispiele?.length > 0) {
    return (
      <ul className="mt-1 text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
        {stat.beispiele.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
  }
  if (typeof stat.durchschnitt === "number") {
    return <p className="text-xs">Min {stat.min} · Max {stat.max} · Ø {stat.durchschnitt}</p>;
  }
  return <p className="text-xs text-muted-foreground">Noch keine Antworten.</p>;
};
